import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Users, Loader2, AlertTriangle, CheckCircle2, Sparkles, LogIn, UserPlus } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { previewTeamInvite, acceptTeamInvite } from '../services/api';

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoggedIn, refreshUser } = useAuth();
  const token = params.get('token');

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!token) { setError('Missing invite token'); setLoading(false); return; }
    (async () => {
      try {
        const res = await previewTeamInvite({ token });
        setPreview(res?.data || res);
      } catch (e) {
        setError(e?.message || 'Invalid or expired invite');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const emailMismatch = isLoggedIn && preview && user?.email
    && user.email.toLowerCase() !== (preview.invitedEmail || '').toLowerCase();

  async function accept() {
    try {
      setAccepting(true); setError(null);
      await acceptTeamInvite({ token });
      // Refresh JWT so teamId/teamRole are picked up
      await refreshUser();
      setAccepted(true);
      setTimeout(() => navigate('/team'), 1500);
    } catch (e) {
      setError(e?.message || 'Failed to accept invite');
    } finally {
      setAccepting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-900 p-4">
      <div className="max-w-md w-full">
        <div className="relative overflow-hidden bg-gradient-to-br from-violet-500/10 via-dark-800/90 to-dark-900 rounded-2xl border border-violet-500/20 p-8">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-violet-400/10 blur-3xl pointer-events-none" />

          <div className="relative">
            <div className="w-14 h-14 rounded-xl bg-violet-500/20 border border-violet-400/30 flex items-center justify-center mb-4">
              <Users className="w-7 h-7 text-violet-400" />
            </div>

            <h1 className="text-2xl font-bold text-white mb-1">Team Invitation</h1>
            <p className="text-xs text-violet-200/70 flex items-center gap-1.5 mb-6">
              <Sparkles className="w-3 h-3" /> haythix Elite · Trader's Suite
            </p>

            {loading && (
              <div className="text-sm text-gray-400 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Verifying invite...
              </div>
            )}

            {error && !accepted && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-300 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold">{error}</div>
                  <div className="text-red-300/80 text-xs mt-1">If you believe this is wrong, ask the team admin to send a fresh invite.</div>
                </div>
              </div>
            )}

            {accepted && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-sm text-emerald-300 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold">Joined!</div>
                  <div className="text-emerald-300/80 text-xs mt-1">Redirecting to your team workspace...</div>
                </div>
              </div>
            )}

            {preview && !accepted && !error && (
              <>
                <div className="space-y-3 mb-6">
                  <Row label="Team" value={preview.teamName} />
                  <Row label="Plan" value={preview.teamPlan ? preview.teamPlan.replace('_', ' ') : '—'} className="capitalize" />
                  <Row label="Your role" value={preview.teamRole} className="capitalize" />
                  <Row label="Invited email" value={preview.invitedEmail} />
                  <Row label="Expires" value={new Date(preview.expiresAt).toLocaleString()} />
                </div>

                {!isLoggedIn && (
                  <div className="space-y-2">
                    <div className="text-xs text-gray-400 mb-2">
                      Sign in with <span className="text-white font-mono">{preview.invitedEmail}</span> to accept this invite. If you don't have an account yet, create one first using the same email.
                    </div>
                    <Link
                      to={`/login?redirect=${encodeURIComponent(`/accept-invite?token=${token}`)}`}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-violet-500/20 border border-violet-500/40 text-violet-200 hover:bg-violet-500/30 text-sm font-medium transition"
                    >
                      <LogIn className="w-4 h-4" /> Sign in to accept
                    </Link>
                    <Link
                      to={`/register?email=${encodeURIComponent(preview.invitedEmail)}&redirect=${encodeURIComponent(`/accept-invite?token=${token}`)}`}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-dark-800/80 border border-dark-700 text-gray-300 hover:bg-dark-700 text-sm transition"
                    >
                      <UserPlus className="w-4 h-4" /> Create account
                    </Link>
                  </div>
                )}

                {isLoggedIn && emailMismatch && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm text-amber-200 mb-3">
                    This invite was sent to <strong>{preview.invitedEmail}</strong>, but you're signed in as <strong>{user.email}</strong>. Sign out and sign back in with the invited email to accept.
                  </div>
                )}

                {isLoggedIn && !emailMismatch && (
                  <button
                    onClick={accept}
                    disabled={accepting}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-violet-500/20 border border-violet-500/40 text-violet-200 hover:bg-violet-500/30 text-sm font-semibold transition disabled:opacity-50"
                  >
                    {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Join {preview.teamName}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="text-center mt-4">
          <Link to="/" className="text-xs text-gray-500 hover:text-gray-300">← Back to haythix</Link>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, className = '' }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[11px] uppercase tracking-wider text-gray-500 font-mono">{label}</span>
      <span className={`text-sm text-white font-medium ${className}`}>{value}</span>
    </div>
  );
}
